/**
 * usePdfPreview — one-liner state helper for any page that needs the
 * Preview + Download flow around a `fetchXxxPdfUrl`-style helper.
 *
 * Usage:
 *   const pdf = usePdfPreview();
 *   <Button onClick={() => pdf.preview(() => fetchInvoicePdfUrl(id))}>Preview</Button>
 *   <Button onClick={() => pdf.download(() => fetchInvoicePdfUrl(id))}>Download</Button>
 *   <PdfPreviewDialog url={pdf.url} filename={pdf.filename} onClose={pdf.close} />
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { triggerDownload } from '../services/api';

export const usePdfPreview = () => {
  const [url, setUrl] = useState(null);
  const [filename, setFilename] = useState('');
  const [busy, setBusy] = useState(false);

  const preview = async (fetcher) => {
    setBusy(true);
    try {
      const res = await fetcher();
      setUrl(res.url);
      setFilename(res.filename);
    } catch (e) {
      toast.error(e?.message || 'Could not load PDF');
    } finally {
      setBusy(false);
    }
  };
  const download = async (fetcher) => {
    setBusy(true);
    try {
      const res = await fetcher();
      triggerDownload(res.url, res.filename);
      setTimeout(() => URL.revokeObjectURL(res.url), 5_000);
    } catch (e) {
      toast.error(e?.message || 'Could not download PDF');
    } finally {
      setBusy(false);
    }
  };
  const close = () => {
    if (url) URL.revokeObjectURL(url);
    setUrl(null);
    setFilename('');
  };

  return { url, filename, busy, preview, download, close };
};

export default usePdfPreview;
